import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { OtpCodeField } from "@/components/auth/OtpCodeField";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { AuthPageBrand } from "@/components/shared/AuthPageBrand";
import { getPasswordPolicyErrorMessage, getPasswordRuleItems, passwordMeetsAllRules } from "@/lib/passwordPolicy";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { AUTH_LOGO_INLINE } from "@/lib/authLogoInline";

const ResetPasswordPage = () => {
  const location = useLocation();
  const initialEmail = useMemo(() => new URLSearchParams(location.search).get("email") || "", [location.search]);
  const [step, setStep] = useState(1); // 1: email, 2: code, 3: new password
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { requestPasswordReset, resetPassword } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const passwordRuleItems = getPasswordRuleItems(language, newPassword);
  const passwordRulesSatisfied = passwordMeetsAllRules(newPassword);

  const getResetErrorMessage = (error, fallback) => {
    const detail = error?.response?.data?.detail ?? error?.response?.data ?? null;

    if (typeof detail === "string" && detail.trim()) return detail;

    if (Array.isArray(detail)) {
      const messages = detail
        .map((item) => {
          if (typeof item === "string") return item;
          if (item && typeof item === "object") return item.msg || item.message || null;
          return null;
        })
        .filter(Boolean);

      if (messages.length) return messages.join(" · ");
    }

    if (detail && typeof detail === "object") {
      return detail.msg || detail.message || fallback;
    }

    if (error?.code === "ERR_NETWORK") {
      return "Could not reach the server. Please try again.";
    }

    return fallback;
  };

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await requestPasswordReset(email);
      toast.success("Reset code sent to your email");
      setStep(2);
    } catch (error) {
      console.error("Reset request error:", error);
      toast.error(getResetErrorMessage(error, "Failed to send reset code. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = (e) => {
    e.preventDefault();
    if (code.length === 6) {
      setStep(3);
    } else {
      toast.error("Please enter the 6-digit code");
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (!passwordRulesSatisfied) {
      toast.error(getPasswordPolicyErrorMessage(language));
      return;
    }

    setLoading(true);

    try {
      await resetPassword(email, code, newPassword);
      toast.success("Password reset successful!");
      navigate("/login");
    } catch (error) {
      console.error("Password reset error:", error);
      toast.error(getResetErrorMessage(error, "Failed to reset password. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen overflow-y-auto bg-background flex items-start md:items-center justify-center px-4 sm:px-6 py-8 md:py-10 hero-gradient"
      data-testid="reset-password-page"
      lang={language}
      translate="no"
    >
      <AuthPageBrand testIdPrefix="reset-page-brand" />

      <Card className="notranslate w-full max-w-md bg-card border-border p-6 md:p-7" translate="no">
        <div className="text-center mb-6 md:mb-7">
          <Link to="/" className="inline-flex justify-center mb-6" data-testid="reset-card-logo-link">
            <img
              src={AUTH_LOGO_INLINE}
              alt="Voxara"
              className="h-28 sm:h-32 md:h-36 w-auto max-w-full object-contain"
              width="960"
              height="640"
              loading="eager"
              fetchPriority="high"
              decoding="sync"
              data-testid="reset-card-logo-image"
            />
          </Link>
          <h1 className="text-2xl font-bold">Reset Password</h1>
          <p className="text-muted-foreground mt-2">
            {step === 1 && "Enter your email to receive a reset code"}
            {step === 2 && "Enter the 6-digit code sent to your email"}
            {step === 3 && "Create a new password"}
          </p>
        </div>

        {step === 1 && (
          <form onSubmit={handleRequestCode} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-background border-border focus:border-primary"
                data-testid="reset-email-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 btn-glow"
              disabled={loading}
              data-testid="reset-send-code-btn"
              translate="no"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending code...
                </>
              ) : (
                "Send Reset Code"
              )}
            </Button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleVerifyCode} className="space-y-6">
            <div className="space-y-2">
              <Label>Verification Code</Label>
              <div className="flex justify-center">
                <OtpCodeField
                  value={code}
                  onChange={setCode}
                  testId="reset-code-input"
                  fallbackTestId="reset-code-input-fallback"
                  ariaLabel="Reset password verification code"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 btn-glow"
              data-testid="reset-verify-code-btn"
              translate="no"
            >
              Verify Code
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => setStep(1)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={handleResetPassword} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  className="bg-background border-border focus:border-primary pr-10"
                  data-testid="reset-new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              <div className="space-y-1" data-testid="reset-password-rule-list">
                {passwordRuleItems.map((rule) => (
                  <p
                    key={rule.key}
                    className={`text-xs font-medium transition-colors ${rule.satisfied ? "text-emerald-600" : "text-amber-600"}`}
                    data-testid={`reset-password-${rule.key}-message`}
                  >
                    {rule.satisfied ? "✓ " : "• "}
                    {rule.label}
                  </p>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                className="bg-background border-border focus:border-primary"
                data-testid="reset-confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 btn-glow"
              disabled={loading}
              data-testid="reset-password-btn"
              translate="no"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Resetting password...
                </>
              ) : (
                "Reset Password"
              )}
            </Button>
          </form>
        )}

        <div className="mt-6 text-center text-sm text-muted-foreground">
          Remember your password?{" "}
          <Link
            to="/login"
            className="text-primary hover:underline font-medium"
          >
            Sign in
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
